import React, { useEffect, useState } from 'react';
import {
  TextInput,
  BooleanInput,
  ArrayInput,
  SimpleFormIterator,
  AutocompleteInput,
  FormDataConsumer,
  required,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { Typography, Box, useTheme } from '@mui/material';
import axios from '../axiosInstance';

const RecipesFormFields = ({ fileOptions, companyID }) => {
  const theme = useTheme();
  const bgColor = theme.palette.background.paper;

  const imageChoices = fileOptions?.Images?.map((name) => ({ id: name, name })) || [];
  const pdfChoices = fileOptions?.Pdfs?.map((name) => ({ id: name, name })) || [];

  const [videoThumbnails, setVideoThumbnails] = useState({});
  const [videoChoices, setVideoChoices] = useState([]);

  const getFileUrl = (filename) => {
    if (!filename) return '';
    return `${axios.defaults.baseURL}/images/${companyID}/${filename}`;
  };

  const generateVideoThumbnail = (videoUrl) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.currentTime = 0.1;

      video.onloadeddata = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageUrl = canvas.toDataURL('image/png');
        resolve(imageUrl);
      };

      video.onerror = (err) => reject(err);
    });
  };

  useEffect(() => {
    const loadThumbnails = async () => {
      const thumbs = {};
      const choices = [];

      for (const name of fileOptions?.Videos || []) {
        const url = getFileUrl(name);
        try {
          const thumb = await generateVideoThumbnail(url);
          thumbs[name] = thumb;
          choices.push({ id: name, name, thumbnail: thumb });
        } catch (err) {
          console.error(`Thumbnail error for ${name}:`, err);
          choices.push({ id: name, name, thumbnail: null });
        }
      }

      setVideoThumbnails(thumbs);
      setVideoChoices(choices);
    };

    loadThumbnails();
  }, [fileOptions, companyID]);

  const ImageSelectorWithPreview = ({ source, label, choices }) => {
    const selected = useWatch({ name: source });
    return (
      <Box mb={2} sx={{ width: '100%' }}>
        <AutocompleteInput
          source={source}
          label={label}
          choices={choices}
          fullWidth
          optionText={(choice) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img
                src={getFileUrl(choice.name)}
                alt={choice.name}
                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                onError={(e) => { e.currentTarget.src = ''; }}
              />
              <span>{choice.name}</span>
            </Box>
          )}
          getOptionLabel={(option) => option?.name || ''}
          inputText={(choice) => choice?.name || ''}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img
                src={getFileUrl(option.name)}
                alt={option.name}
                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                onError={(e) => { e.currentTarget.src = ''; }}
              />
              {option.name}
            </Box>
          )}
          validate={required()}
        />
        {selected && (
          <Box mt={1}>
            <img
              src={getFileUrl(selected)}
              alt={selected}
              style={{ width: 'auto', height: 80, borderRadius: 4, border: '1px solid #ccc', objectFit: 'contain' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const VideoSelectorWithPreview = ({ source, label, choices }) => {
    const selected = useWatch({ name: source });

    return (
      <Box mb={2} sx={{ width: '100%' }}>
        <AutocompleteInput
          source={source}
          label={label}
          choices={choices}
          fullWidth
          getOptionLabel={(option) => option?.name || ''}
          inputText={(choice) => choice?.name || ''}
          optionText={(choice) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {choice.thumbnail ? (
                <img
                  src={choice.thumbnail}
                  alt={choice.name}
                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                />
              ) : (
                <Box sx={{ width: 40, height: 40, bgcolor: 'grey.300' }} />
              )}
              <span>{choice.name}</span>
            </Box>
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {option.thumbnail ? (
                <img
                  src={option.thumbnail}
                  alt={option.name}
                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                />
              ) : (
                <Box sx={{ width: 40, height: 40, bgcolor: 'grey.300' }} />
              )}
              {option.name}
            </Box>
          )}
          validate={required()}
        />
        {selected && (
          <Box mt={1}>
            {videoThumbnails[selected] && (
            <Box mt={1}>
              <img
                src={videoThumbnails[selected]}
                alt={selected}
                style={{ width: 'auto', height: 80, borderRadius: 4, objectFit: 'contain', border: '1px solid #ccc' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </Box>
          )}
          </Box>
        )}
      </Box>
    );
  };

  const PdfSelector = ({ source, label, choices }) => (
    <Box mb={2} sx={{ width: '100%' }}>
      <AutocompleteInput
        source={source}
        label={label}
        choices={choices}
        fullWidth
        getOptionLabel={(option) => option?.name || ''}
        validate={required()}
      />
    </Box>
  );

  return (
    <>
      <TextInput source="Name" label="Nome" validate={required()} fullWidth />
      <TextInput source="Title" label="Título" validate={required()} fullWidth />
      <TextInput source="Description" label="Descrição" validate={required()} fullWidth />
      <TextInput source="Servings" label="Porções" />
      <TextInput source="Time" label="Tempo" validate={required()} />
      <TextInput source="Difficulty" label="Dificuldade" validate={required()} />

      <PdfSelector source="Pdf" label="PDF" choices={pdfChoices} />
      <ImageSelectorWithPreview source="Image" label="Imagem" choices={imageChoices} />
      <BooleanInput source="GlutenFree" label="Sem Glúten" />
      <BooleanInput source="Vegetarian" label="Vegetariano" />
      <VideoSelectorWithPreview source="Video" label="Vídeo" choices={videoChoices} />

      <TextInput source="Calories" label="Calorias" validate={required()} />
      <TextInput source="Scale" label="Escala" validate={required()} />
      <TextInput source="SaturatedFat" label="Gordura Saturada" validate={required()} />
      <TextInput source="Fat" label="Gordura Total" validate={required()} />
      <TextInput source="Protein" label="Proteína" validate={required()} />
      <TextInput source="Fiber" label="Fibra" validate={required()} />
      <TextInput source="Sugars" label="Açúcares" validate={required()} />
      <TextInput source="Carbohydrates" label="Carboidratos" validate={required()} />

      <Typography variant="h6" gutterBottom>Ingredientes</Typography>
      <ArrayInput source="ingredients" label={false}>
        <SimpleFormIterator>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2, backgroundColor: bgColor }}>
            <TextInput source="Name" label="Ingrediente" validate={required()} />
            <TextInput source="Quantity" label="Quantidade" validate={required()} />
          </Box>
        </SimpleFormIterator>
      </ArrayInput>

      <Typography variant="h6" gutterBottom>Passos</Typography>
      <ArrayInput source="steps" label={false}>
        <SimpleFormIterator>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2, backgroundColor: bgColor }}>
            <TextInput source="Description" label="Descrição do Passo" validate={required()} fullWidth />
          </Box>
        </SimpleFormIterator>
      </ArrayInput>

      <Typography variant="h6" gutterBottom>Imagens</Typography>
      <ArrayInput source="images" label={false}>
        <SimpleFormIterator>
          <FormDataConsumer>
            {({ scopedFormData }) => (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 2, backgroundColor: bgColor }}>
                <AutocompleteInput
                  source="Name"
                  label="Nome da Imagem"
                  choices={imageChoices}
                  fullWidth
                  getOptionLabel={(option) => option?.name || ''}
                  inputText={(choice) => choice?.name || ''}
                  optionText={(choice) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src={getFileUrl(choice.name)}
                        alt={choice.name}
                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                        onError={(e) => { e.currentTarget.src = ''; }}
                      />
                      <span>{choice.name}</span>
                    </Box>
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src={getFileUrl(option.name)}
                        alt={option.name}
                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                        onError={(e) => { e.currentTarget.src = ''; }}
                      />
                      {option.name}
                    </Box>
                  )}
                  validate={required()}
                />
                {scopedFormData?.Name && (
                  <Box mt={1}>
                    <img
                      src={getFileUrl(scopedFormData.Name)}
                      alt={scopedFormData.Name}
                      style={{ width: 'auto', height: 80, borderRadius: 4, border: '1px solid #ccc', objectFit: 'contain' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </FormDataConsumer>
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

export default RecipesFormFields;
